import React, { useState, useEffect } from 'react';

interface ProgressiveImageProps {
  src: string;
  placeholderSrc: string;
  alt: string;
  className?: string;
  aspectRatio?: string; // e.g., '16:9', '4:3', '1:1'
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Tesla-inspired progressive image component
 * Loads a low-resolution placeholder first, then transitions to the high-resolution image
 */
const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  src,
  placeholderSrc,
  alt,
  className = '',
  aspectRatio = '16:9',
  onLoad,
  onError,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [paddingBottom, setPaddingBottom] = useState('56.25%'); // Default 16:9

  // Calculate padding-bottom based on aspect ratio
  useEffect(() => {
    if (aspectRatio) {
      const [width, height] = aspectRatio.split(':').map(Number);
      if (width && height) {
        setPaddingBottom(`${(height / width) * 100}%`);
      }
    }
  }, [aspectRatio]);

  // Handle high-resolution image load
  const handleImageLoad = () => {
    setIsLoaded(true);
    if (onLoad) {
      onLoad();
    }
  };

  // Handle image error
  const handleImageError = () => {
    setHasError(true);
    if (onError) {
      onError();
    }
  };

  return (
    <div 
      className={`progressive-image ${className}`} 
      style={{ paddingBottom }}
    >
      {/* Low-quality placeholder image */}
      <img
        src={placeholderSrc}
        alt={alt}
        className="progressive-image__placeholder"
        style={{ opacity: isLoaded ? 0 : 1 }}
      />
      
      {/* High-quality main image */}
      {!hasError && (
        <img
          src={src}
          alt={alt}
          className={`progressive-image__image ${isLoaded ? 'loaded' : ''}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      )}
    </div>
  );
};

export default ProgressiveImage;